import { TStore } from "@omni/components/redux/store.types";
import { Config } from "@omni/components/utils/config.utils";
import {
    ErrorBoundary,
    ErrorBoundaryFallbackComponent
} from "@omni/components/utils/errorBoundary";
import { parseElementForCommentedContent } from "@omni/components/utils/ssr";
import { isEnum } from "@omni/utils/enum.utils";
import { isEmpty } from "lodash-es";
import React from "react";
import ReactDOMClient from "react-dom/client";
import { Provider } from "react-redux";

import { SsrComponent } from "@/client/SsrComponents";
import { ComponentMap } from "@/client/tools/hydrator/@types/hydrator.types";

const MFGraniteProvider = React.lazy(() =>
    import(
        "@omni/components/redux/modules/value/translations/translations.provider"
    ).then((module) => ({
        default: module.GraniteProvider
    }))
);

const MFFeatureToggleProvider = React.lazy(() =>
    import("@omni/components/context/featureToggles.context").then(
        (module) => ({
            default: module.FeatureToggleContextProvider
        })
    )
);

const filterByComponentTypeIfPresent = (
    componentType: string,
    filteredType?: string
) => {
    return !!(
        !filteredType ||
        (filteredType && filteredType === componentType)
    );
};

const componentIsAlreadyHydrated = (element: HTMLElement) => {
    let isHydrated = false;
    for (const _prop in element) {
        if (_prop.indexOf("_react") > -1) {
            isHydrated = true;
        }
    }
    return !isHydrated;
};

const getComponents = (componentKey: string) => {
    return Array.from(
        document.querySelectorAll<HTMLElement>(`[data-ssr=${componentKey}]`)
    )
        .filter((c) => c.closest(".js-modal") === null)
        .filter(componentIsAlreadyHydrated)
        .reduce<{
            root: Array<HTMLElement>;
            nested: Array<HTMLElement>;
        }>(
            (components, element) => {
                const isRoot = !element.parentElement?.closest("[data-ssr]");

                if (isRoot) {
                    components.root.push(element);
                } else {
                    components.nested.push(element);
                }

                return components;
            },
            { root: [], nested: [] }
        );
};

const SSR = (store: TStore, componentType?: string) => {
    const hydrationOrder: Array<string> = [
        "SEARCH_BAR",
        "E_COMMERCE",
        "PRODUCT_PAGE",
        "IMAGE_VIEWER",
        "CART_BUTTON",
        "PRODUCT_SLIDER",
        "PRODUCT_TILE",
        "HEADER_BUTTON",
        "ACCOUNT_BUTTON",
        "WISHLIST_HEADER_BUTTON",
        "FLYOUT_BUTTON",
        "SHOP_FINDER_HEADER_BUTTON",
        "CATEGORIES_SLIDER",
        "IMAGE_VIEWER_FULL_WIDTH",
        "SAFETY_INFO",
        "LANGUAGE_SELECTOR",
        "DEBUG_NOTIFICATION",
        "PRODUCT_COMPARISON",
        "PRODUCT_COMPARISON_BAR",
        "PRODUCT_BUNDLES",
        "STORE_LOCATOR_MOBILE",
        "CALL_TO_ACTION",
        "STORE_SEARCH",
        "STORE_DIRECTIONS",
        "NEWSLETTER",
        "NEWSLETTER_SUBSCRIPTION",
        "LOGIN_FORM",
        "REGISTER_FORM",
        "RESET_PASSWORD_FORM",
        "REGISTER_CHECKOUT_FORM",
        "FRANCHISE_REQUEST",
        "CONTACT_CUSTOMER_SERVICE",
        "CREATE_GIFT_CARD",
        "BUSINESS_GIFT_CARD_REQUEST",
        "GIFT_CARD_REQUEST_BALANCE",
        "BONUS_CARD_REQUEST",
        "BONUS_CARD_REQUEST_BALANCE",
        "CUSTOMER_LOAD",
        "QUIZZI_APPOINTMENT",
        "SEARCH_RESULTS",
        "RECOVERABILITY_INDEX",
        "E_CORNER_PAYMENT",
        "ADD_TO_CART_MODAL",
        "KEY_FEATURES",
        "IMAGE_LINK",
        "IMAGE",
        "PRODUCT_FEATURES",
        "BANNER_SLIDER",
        "BRAND_LOGO",
        "PAINT_CONFIGURATOR"
    ].filter((cmpType) =>
        filterByComponentTypeIfPresent(cmpType, componentType)
    );

    const storeRef = store;

    hydrationOrder
        .reduce<Promise<Map<string, Array<HTMLElement>>>>(
            (nestedElementsPromise, componentKey) => {
                return nestedElementsPromise.then((nestedElements) => {
                    const { root, nested } = getComponents(componentKey);

                    nestedElements.set(componentKey, nested);

                    return Promise.all([
                        delay(0),
                        doSsrAsync(root, storeRef)
                    ]).then(() => nestedElements);
                });
            },
            Promise.resolve(new Map<string, Array<HTMLElement>>())
        )
        .then((nestedElements) => {
            return hydrationOrder.reduce((promise, componentKey) => {
                return promise.then(() => {
                    const elements = nestedElements.get(componentKey);

                    if (!elements || isEmpty(elements)) {
                        return Promise.resolve();
                    } else {
                        nestedElements.delete(componentKey);

                        return Promise.all([
                            delay(0),
                            doSsrAsync(elements, storeRef)
                        ]).then();
                    }
                });
            }, Promise.resolve());
        })
        .then(() => {
            const images = document.querySelectorAll<HTMLImageElement>(
                'img[loading="lazy"]'
            );

            images.forEach((img) => {
                if (img.dataset.src) {
                    img.src = img.dataset.src;
                }
            });
        });
};

const SSRModal = (store: TStore, element: HTMLElement) => {
    const ssrComponents = Array.from(
        element.querySelectorAll<HTMLElement>("[data-ssr]")
    );
    const storeRef = store;
    doSsr(ssrComponents, storeRef);
};

const doSsr = async (elements: Array<HTMLElement>, store: TStore) => {
    if (elements) {
        const componentMap = window.hydrator.getComponentMap(
            "SSR"
        ) as ComponentMap;

        for (const component of elements) {
            const componentName = component.dataset.ssr;

            if (!isEnum(SsrComponent, componentName)) {
                console.log("SSR component does not exist - ", componentName);
                return;
            }

            let Component = componentMap[componentName];

            if (!Component) {
                console.warn(`SSR::MISSING COMPONENT - ${componentName}`);

                const { SsrComponentMap } = await import(
                    "@/client/SsrComponents"
                );

                Component = SsrComponentMap[componentName];
            }

            if (!Component) {
                console.log("Component nowhere to be found - ", componentName);
                return;
            }

            const uuid = component.dataset.ssrComponentUuid;

            const componentPropsElement = document.querySelector<HTMLElement>(
                `script[data-ssr-data-uuid='${uuid}']`
            );
            const componentProps = parseElementForCommentedContent(
                componentPropsElement
            );

            ReactDOMClient.hydrateRoot(
                component,
                <ErrorBoundary
                    FallbackComponent={ErrorBoundaryFallbackComponent}
                >
                    <React.Suspense>
                        <Provider store={store}>
                            <MFGraniteProvider>
                                <MFFeatureToggleProvider
                                    serverSideFeatureToggles={
                                        Config["feature-toggles"]
                                    }
                                >
                                    <Component {...componentProps} />
                                </MFFeatureToggleProvider>
                            </MFGraniteProvider>
                        </Provider>
                    </React.Suspense>
                </ErrorBoundary>,
                {
                    identifierPrefix: uuid,
                    onRecoverableError(error) {
                        console.log(
                            `Error during hydration at component: ${componentName}`
                        );
                        console.error(error);
                    }
                }
            );
        }
    }
};

const doSsrAsync = (elements: Array<HTMLElement>, store: TStore) => {
    return new Promise<void>((resolve) => {
        doSsr(elements, store).then(resolve);
    });
};

const delay = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

export { SSR, SSRModal };
